import { inject, Injectable } from '@angular/core'
import { EVENT } from '@app/analytics/analytics.cnst'
import { AnalyticsService } from '@app/analytics/analytics.service'
import { isAndroidApp } from '@app/cnst/userDevice.cnst'
import { tryCatch } from '@app/decorators/tryCatch.decorator'
import { logUtil } from '@app/util/log.util'
import { dataViewToNumbers, numbersToDataView } from '@capacitor-community/bluetooth-le'
import { localTime, UnixTimestamp } from '@naturalcycles/js-lib'
import { BluetoothClient } from '@src/typings/capacitor'
import { Subject } from 'rxjs'
import { LogService } from './log.service.js'

export interface SyncedTemperature {
  temperature: number
  timestamp: UnixTimestamp
  successMsgId?: number
  temperatureRawCelsius?: number
}

/**
 * Contains methods for thermometer-specific Bluetooth operations
 * Depends on BleClient for @capacitor-community/bluetooth-le
 */
@Injectable({ providedIn: 'root' })
export class BluetoothThermometerService {
  private analyticsService = inject(AnalyticsService)
  private logService = inject(LogService)

  public temperatures$ = new Subject<SyncedTemperature[]>()

  constructor() {
    this.temperatures$.subscribe(temperatures => {
      temperatures.forEach(temperature => {
        this.analyticsService.trackEvent(EVENT.BLUETOOTH_TEMPERATURE_SYNCED, {
          ...temperature,
          temperatureMeasuredTimestamp: localTime(temperature.timestamp).toUTC().toPretty(),
          temperatureSyncedTimestamp: localTime.now().toUTC().toPretty(),
        })
      })
    })
  }

  public async write(
    deviceId: string,
    service: string,
    characteristic: string,
    value: number[],
    description?: string,
  ): Promise<void> {
    this.logService.logBleAction(
      `Writing to: ${deviceId}; Service: ${service}; Characteristic: ${characteristic}; Data: ${value}`,
    )
    const dataView = numbersToDataView(value)

    const eventBody = { deviceId, service, characteristic, value, description }

    if (isAndroidApp) {
      return await BluetoothClient.writeWithoutResponse(deviceId, service, characteristic, dataView)
        .then(() => {
          this.analyticsService.trackEvent(EVENT.BLUETOOTH_WRITE, { ...eventBody, failed: false })
        })
        .catch(err => {
          logUtil.error(`[ble][error] write ${err.message}`)

          this.analyticsService.trackEvent(EVENT.BLUETOOTH_WRITE, {
            ...eventBody,
            failed: true,
            failReason: err.message,
          })
        })
    }

    return await BluetoothClient.write(deviceId, service, characteristic, dataView)
      .then(() => {
        this.analyticsService.trackEvent(EVENT.BLUETOOTH_WRITE, { ...eventBody, failed: false })
      })
      .catch(err => {
        if (err.message.startsWith('Write timeout')) {
          logUtil.log('[ble][error] write', err.message)
        } else {
          logUtil.error(`[ble][error] write ${err.message}`)
        }

        this.analyticsService.trackEvent(EVENT.BLUETOOTH_WRITE, {
          ...eventBody,
          failed: true,
          failReason: err.message,
        })
      })
  }

  @tryCatch({
    alert: false,
    onError: error => {
      logUtil.error(`[ble][error] read ${error.message}`)
    },
  })
  public async read(
    deviceId: string,
    service: string,
    characteristic: string,
    description?: string,
  ): Promise<DataView> {
    const data = await BluetoothClient.read(deviceId, service, characteristic)
    this.logService.logBleAction(
      `Reading from: ${deviceId}; Service: ${service}, Characteristic: ${characteristic}, Description: ${description}, Data: ${dataViewToNumbers(
        data,
      )}`,
    )

    const eventBody = {
      deviceId,
      service,
      characteristic,
      description,
      dataNumbers: dataViewToNumbers(data),
    }
    this.analyticsService.trackEvent(EVENT.BLUETOOTH_READ, { ...eventBody })

    return data
  }
}
